// import { TOKEN_NAME } from "./context/ApiAuthContext";
import React, { createContext, useContext, useEffect, useState } from "react";
import {
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from "@microsoft/signalr";

class ReconnectionPolicy {
  nextRetryDelayInMilliseconds() {
    return 10000;
  }
}

const SignalRContext = createContext(undefined);

export function SignalRProvider({
  server,
  context,
  token,
  tokenFactory,
  children,
}) {
  const {
    user: { customerId, groupId, tenantId, userId },
  } = React.useContext(context);

  const [connection, setConnection] = useState(null);
  const [error, setError] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    const options = {
      accessTokenFactory: async () => {
        return await tokenFactory();
      },
    };
    const newConnection = new HubConnectionBuilder()
      .withUrl(server, tokenFactory && options)
      .withAutomaticReconnect(new ReconnectionPolicy())
      .configureLogging(LogLevel.Debug)
      .build();

    setConnection(newConnection);
    window.signalR = newConnection;
  }, [server, token]);

  useEffect(() => {
    async function restartConnection() {
      connection
        .start()
        .then(async () => {
          try {
            setError(null);
            await connection.send("login", customerId, tenantId);
            await connection.send(
              "broadcastLogin",
              customerId,
              groupId,
              tenantId,
              userId
            );
          } catch (e) {
            setError(e);
          }
        })
        .catch((e) => {
          console.error("Connection Error ", e);
          setError(e);
        });
    }

    if (connection && customerId && tenantId) {
      connection.onreconnecting((error) => {
        console.error("onreconnecting error", connection.state, error);
        setError(error);
      });
      connection.onclose((e) => {
        setError(e);
        console.error("onclose", e);
      });
      connection.onreconnected(() => {
        setError(null);
        connection.send("login", customerId, tenantId);
        connection.send(
          "broadcastLogin",
          customerId,
          groupId,
          tenantId,
          userId
        );
      });

      connection.on("login", () => {
        setIsLoggedIn(true);
      });

      restartConnection();
    }
  }, [connection, customerId, tenantId, userId, groupId]);

  return (
    <SignalRContext.Provider
      value={{
        connection,
        isConnected: connection?.state === HubConnectionState.Connected,
        isLoggedIn,
        isError: !!error,
        error,
      }}
    >
      {children}
    </SignalRContext.Provider>
  );
}

export function useSignalR() {
  return useContext(SignalRContext);
}
